import React, { useEffect, useMemo, useState } from "react";
import {
  getTimetableRun, listClasses, listSubjects, listStaff, getPeriodTimes, getStaffBusyWeek, getPeriodNotes,
  getTeacherAbsences, setTeacherAbsence, removeTeacherAbsence, getSubstitutionLog, TEACHER_DEPARTMENT,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import HappySchoolCellSubstituteDialog from "./HappySchoolCellSubstituteDialog";
import { buildSessions, busyKeyToPairs } from "./HappySchoolAllTeachersView";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase();
const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return isoOf(d); };
// "5" / "1-3" / "1,2,5" (tiết 1-10) -> mảng chỉ số 0-based.
const parsePeriods = (txt) => {
  const out = new Set();
  String(txt || "").split(/[,;\s]+/).filter(Boolean).forEach((part) => {
    const m = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!m) return;
    const a = Number(m[1]), b = m[2] ? Number(m[2]) : a;
    for (let i = Math.min(a, b); i <= Math.max(a, b); i++) if (i >= 1 && i <= 10) out.add(i - 1);
  });
  return [...out].sort((x, y) => x - y);
};
const ddmm = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;


// "TKB Giáo viên — Dạy Thay": danh sách GV bận/vắng theo NGÀY và các tiết của họ cần người dạy thay.
// Admin thêm GV vắng vào ngày, rồi bấm "Xử lý" ở từng tiết để mở dialog dạy thay (cùng dialog nhấn đúp ở TKB Giáo viên — Toàn trường).
const HappySchoolSubstituteBoard = ({ runId, weekId, startDate, canEdit = false }) => {
  const schoolId = getActiveSchoolId();
  const todayIso = isoOf(new Date());
  const weekDates = useMemo(() => (startDate ? [0, 1, 2, 3, 4, 5].map((i) => addDays(startDate, i)) : []), [startDate]);
  const [date, setDate] = useState(() => (startDate && [0, 1, 2, 3, 4, 5].map((i) => addDays(startDate, i)).includes(todayIso) ? todayIso : startDate || todayIso));
  const [dateChosen, setDateChosen] = useState(false); // người dùng đã tự bấm chọn ngày -> không tự nhảy ngày nữa
  const day = weekDates.indexOf(date);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busyWeek, setBusyWeek] = useState({});
  const [periodNotes, setPeriodNotes] = useState({});
  const [absByDate, setAbsByDate] = useState({}); // { "YYYY-MM-DD": [{teacherId, note}] } — GV bận admin đã thêm tay
  const [log, setLog] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [subTarget, setSubTarget] = useState(null);
  const [q, setQ] = useState("");
  const [pickTid, setPickTid] = useState("");
  const [note, setNote] = useState("");
  const [periodsText, setPeriodsText] = useState(""); // "5" / "1-3" / "1,2,5" (số tiết 1-10); trống = cả ngày
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!schoolId || !runId) return;
    setLoading(true); setError("");
    Promise.all([
      getTimetableRun(schoolId, runId), listClasses(schoolId), listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT }), getPeriodTimes(schoolId),
      weekId ? getStaffBusyWeek(schoolId, weekId) : Promise.resolve({}),
      weekId ? getPeriodNotes(schoolId, weekId, runId).catch(() => ({})) : Promise.resolve({}),
      Promise.all(weekDates.map((dt) => getTeacherAbsences(schoolId, dt).then((r) => [dt, (r && r.absences) || []]).catch(() => [dt, []]))),
      weekId ? getSubstitutionLog(schoolId, weekId).catch(() => ({ entries: [] })) : Promise.resolve({ entries: [] }),
    ])
      .then(([r, c, s, t, pt, bw, pn, ab, lg]) => {
        setRun(r); setClasses(c); setSubjects(s);
        setTeachers(t.slice().sort((a, b) => a.name.localeCompare(b.name, "vi")));
        setSessions(buildSessions(pt)); setBusyWeek(bw || {}); setPeriodNotes(pn || {});
        setAbsByDate(Object.fromEntries(ab || [])); setLog((lg && lg.entries) || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId, weekDates, reloadKey]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const teacherById = useMemo(() => Object.fromEntries(teachers.map((t) => [t.id, t])), [teachers]);
  const grid = (run && run.grid) || {};
  const isBusyAt = (teacherId, d, global) => {
    const b = busyWeek[teacherId];
    return !!b && Object.keys(b).some((k) => busyKeyToPairs(k, sessions).some(([dd, p]) => dd === d && p === global));
  };
  const sessionLabelOf = (global) => {
    const s = sessions.find((x) => x.periods.some((p) => p.global === global));
    return s ? s.label : "";
  };

  // GV bận của TỪNG NGÀY = danh sách admin thêm tay + các GV có tiết ĐÃ được xử lý dạy thay trong nhật ký (tự động,
  // gốc chuỗi đổi = fromTeacherId của lần đổi đầu) — để mở bảng là thấy ngay các GV đang cần/đã dạy thay.
  const absencesByDay = useMemo(() => {
    const out = weekDates.map((dt) => (absByDate[dt] || []).map((a) => ({ ...a, auto: false })));
    const chains = {};
    log.forEach((e) => { if (e.mode !== "assign" && e.fromTeacherId && e.day >= 0 && e.day < 6) (chains[`${e.day}|${e.classId}|${e.period}`] = chains[`${e.day}|${e.classId}|${e.period}`] || []).push(e); });
    Object.values(chains).forEach((arr) => {
      const first = arr[0];
      if (!out[first.day] || out[first.day].some((a) => a.teacherId === first.fromTeacherId)) return;
      out[first.day].push({ teacherId: first.fromTeacherId, note: "", auto: true });
    });
    return out;
  }, [weekDates, absByDate, log]);
  const absences = day >= 0 ? absencesByDay[day] : [];

  // Lần tải đầu: nếu hôm nay không có GV bận nào hoặc có ngày khác nhiều hơn -> nhảy tới ngày (từ hôm nay trở đi) có nhiều GV bận nhất.
  useEffect(() => {
    if (loading || dateChosen) return;
    const todayIdx = weekDates.indexOf(todayIso);
    const from = todayIdx >= 0 ? todayIdx : 0;
    let best = -1, bestN = 0;
    absencesByDay.forEach((a, i) => { if (i >= from && a.length > bestN) { best = i; bestN = a.length; } });
    if (best >= 0 && weekDates[best] !== date) setDate(weekDates[best]);
  }, [loading, absencesByDay]);

  // Mỗi GV vắng -> các tiết của họ trong ngày đang xem: chưa xử lý (ô vẫn là GV đó) + đã xử lý (theo nhật ký).
  const boards = useMemo(() => {
    if (day < 0) return [];
    // Nhật ký theo từng (lớp, tiết) trong ngày, theo thứ tự thời gian. Một tiết có thể bị đổi người nhiều lần
    // (A -> B -> C): GỐC (người vắng) = fromTeacherId của lần đổi ĐẦU, kết quả hiện tại = lần đổi CUỐI.
    const chains = {}; // "classId|period" -> [entries]
    log.forEach((e) => { if (e.day === day) (chains[`${e.classId}|${e.period}`] = chains[`${e.classId}|${e.period}`] || []).push(e); });
    const allPeriods = sessions.flatMap((ses) => ses.periods.map((p) => p.global));
    return absences.map((a) => {
      const lessons = [];
      // GV khai bận đúng một số tiết trong ngày -> chỉ cần dạy thay ĐÚNG các tiết đó (các tiết còn lại cô vẫn dạy bình thường);
      // không khai bận tiết nào (vắng cả ngày do admin thêm tay) -> mọi tiết của cô trong ngày.
      const manualSet = new Set(a.periods || []); // tiết admin ghi rõ khi thêm GV bận (rỗng = cả ngày)
      const busySet = manualSet.size > 0 ? manualSet : new Set(allPeriods.filter((gp) => isBusyAt(a.teacherId, day, gp)));
      Object.entries(grid).forEach(([k, v]) => {
        if (!v || v[1] !== a.teacherId) return;
        const [classId, d, p] = k.split("|");
        if (Number(d) !== day) return;
        if (busySet.size > 0 && !busySet.has(Number(p))) return;
        if (busySet.size === 0 && a.auto) return; // GV chỉ có mặt trong bảng vì đã được dạy thay vài tiết -> chỉ hiện các tiết đó, không liệt kê tiết còn lại
        lessons.push({ classId, period: Number(p), subjectId: v[0], oldSubjectId: v[0], newSubjectId: null, tc: v[2] === true, status: "pending" });
      });
      Object.entries(chains).forEach(([k, arr]) => {
        const [classId, p] = k.split("|");
        const first = arr[0], last = arr[arr.length - 1];
        if (first.fromTeacherId !== a.teacherId) return;
        const cell = grid[`${classId}|${day}|${p}`];
        if (cell && cell[1] === a.teacherId) return; // đã trả lại cho chính GV này -> đã nằm trong danh sách "pending" ở trên
        // Người đang dạy thật = GV hiện ở ô (nếu có); không có thì theo lần đổi cuối.
        const byTid = last.mode === "clear" ? null : ((cell && cell[1]) || last.toTeacherId);
        lessons.push({
          classId, period: Number(p), subjectId: (cell && cell[0]) || last.subjectId || first.fromSubjectId || first.subjectId,
          oldSubjectId: first.fromSubjectId || first.subjectId, newSubjectId: last.mode === "clear" ? null : ((cell && cell[0]) || last.subjectId),
          status: last.mode, byTid, reason: last.reason, changes: arr.length,
        });
      });
      lessons.sort((x, y) => x.period - y.period || (classById[x.classId] || "").localeCompare(classById[y.classId] || "", "vi"));
      return { ...a, lessons, teacher: teacherById[a.teacherId] };
    });
  }, [absences, grid, log, day, classById, teacherById, sessions, busyWeek]);

  const totalNeed = boards.reduce((s, b) => s + b.lessons.filter((l) => l.status === "pending").length, 0);
  const totalDone = boards.reduce((s, b) => s + b.lessons.filter((l) => l.status !== "pending").length, 0);

  const candidates = useMemo(() => {
    const have = new Set(absences.map((a) => a.teacherId));
    const nq = norm(q);
    return teachers.filter((t) => !have.has(t.id) && (!nq || norm(t.name).includes(nq)));
  }, [teachers, absences, q]);

  const add = async () => {
    if (!pickTid) return;
    setBusy(true);
    try { await setTeacherAbsence(schoolId, date, pickTid, note, parsePeriods(periodsText)); setPickTid(""); setNote(""); setPeriodsText(""); setQ(""); setReloadKey((k) => k + 1); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  const del = async (tid) => {
    if (!window.confirm("Bỏ giáo viên này khỏi danh sách bận của ngày này? (Các tiết đã dạy thay vẫn giữ nguyên.)")) return;
    setBusy(true);
    try { await removeTeacherAbsence(schoolId, date, tid); setReloadKey((k) => k + 1); } catch (e) { setError(e.message); }
    setBusy(false);
  };

  if (!startDate) return <p style={{ color: "#6b7280" }}>Tuần này chưa có ngày bắt đầu.</p>;
  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {weekDates.map((dt, i) => (
          <button key={dt} type="button" onClick={() => { setDate(dt); setDateChosen(true); }} style={dt === date ? dayBtnActive : dayBtn}>
            {DAYS[i]} {ddmm(dt)}{dt === todayIso ? " · Hôm nay" : ""}{absencesByDay[i] && absencesByDay[i].length ? ` (${absencesByDay[i].length} GV)` : ""}
          </button>
        ))}
      </div>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? <p style={{ color: "#6b7280" }}>Đang tải...</p> : (
        <>
          <p style={{ margin: "0 0 10px", fontSize: 14 }}>
            <b>{DAYS[day]} {ddmm(date)}</b>: {absences.length} giáo viên bận · <b style={{ color: totalNeed ? "#dc2626" : "#047857" }}>{totalNeed} tiết cần dạy thay</b> · {totalDone} tiết đã xử lý
          </p>

          {canEdit && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: 10, border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", marginBottom: 14 }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm giáo viên bận..." style={{ padding: 6, border: "1px solid #d1d5db", borderRadius: 6, width: 180 }} />
              <select value={pickTid} onChange={(e) => setPickTid(e.target.value)} style={{ padding: 6, border: "1px solid #d1d5db", borderRadius: 6, maxWidth: 280 }}>
                <option value="">— Chọn giáo viên —</option>
                {candidates.slice(0, 200).map((t) => (<option key={t.id} value={t.id}>{t.name}{t.specialty ? ` — ${t.specialty}` : ""}{t.employmentType ? ` (${t.employmentType})` : ""}</option>))}
              </select>
              <input value={periodsText} onChange={(e) => setPeriodsText(e.target.value)} placeholder="Tiết bận (VD 5 hoặc 1-3; trống = cả ngày)" style={{ padding: 6, border: "1px solid #d1d5db", borderRadius: 6, width: 210 }} />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Lý do (ốm, họp, công tác...)" style={{ padding: 6, border: "1px solid #d1d5db", borderRadius: 6, flex: 1, minWidth: 160 }} />
              <button type="button" onClick={add} disabled={!pickTid || busy} style={addBtn}>+ Thêm GV bận</button>
            </div>
          )}

          {boards.length === 0 ? (
            <p style={{ color: "#6b7280" }}>Chưa có giáo viên nào bận trong ngày này.{canEdit ? " Chọn giáo viên ở trên để thêm." : ""}</p>
          ) : boards.map((b) => (
            <div key={b.teacherId} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#fef3c7" }}>
                <b style={{ fontSize: 15 }}>{b.teacher ? b.teacher.name : b.teacherId}</b>
                <span style={{ color: "#6b7280", fontSize: 13 }}>{[b.teacher && b.teacher.specialty, b.teacher && b.teacher.employmentType].filter(Boolean).join(" · ")}</span>
                {b.periods && b.periods.length > 0 && <span style={{ fontSize: 13 }}>— bận tiết {b.periods.map((x) => x + 1).join(", ")}</span>}
                {b.note && <span style={{ fontSize: 13 }}>— {b.note}</span>}
                <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280" }}>{b.lessons.length === 0 ? "Không có tiết cần thay" : ""}</span>
                {b.auto && <span title="Tự động lấy từ nhật ký dạy thay của ngày này" style={{ fontSize: 11, color: "#6b7280" }}>(từ nhật ký)</span>}
                {canEdit && !b.auto && <button type="button" onClick={() => del(b.teacherId)} disabled={busy} title="Bỏ khỏi danh sách" style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }}>✕</button>}
              </div>
              {b.lessons.length > 0 && (
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                  <thead>
                    <tr>{["Tiết", "Buổi", "Lớp", "Môn cũ", "Môn mới", "Giáo viên", ""].map((h, i) => (<th key={i} style={th}>{h}</th>))}</tr>
                  </thead>
                  <tbody>
                    {b.lessons.map((l) => (
                      <tr key={`${l.classId}|${l.period}`} style={{ background: l.status === "pending" ? "#fff" : "#f0fdf4" }}>
                        <td style={td}><b>{l.period + 1}</b></td>
                        <td style={td}>{sessionLabelOf(l.period)}</td>
                        <td style={td}><b>{classById[l.classId] || l.classId}</b></td>
                        <td style={td}>{subjectById[l.oldSubjectId] || ""}{l.tc ? " (TC)" : ""}</td>
                        <td style={{ ...td, fontWeight: l.newSubjectId && l.newSubjectId !== l.oldSubjectId ? 700 : 400, color: l.newSubjectId && l.newSubjectId !== l.oldSubjectId ? "#b45309" : undefined }}>
                          {l.status === "pending" || !l.newSubjectId ? "—" : (subjectById[l.newSubjectId] || "")}
                        </td>
                        <td style={{ ...td, color: l.status === "pending" ? "#dc2626" : "#047857", fontWeight: 600 }}>
                          {l.status === "pending" ? "Chưa có người dạy thay"
                            : l.status === "clear" ? "Để trống"
                            : `${l.byTid && teacherById[l.byTid] ? teacherById[l.byTid].name : ""}${l.status === "supervise" ? " (trông lớp)" : ""}`}
                        </td>
                        <td style={td}>
                          {canEdit && <button type="button" onClick={() => setSubTarget({ classId: l.classId, day, period: l.period })} style={l.status === "pending" ? actBtn : actBtnGhost}>{l.status === "pending" ? "Xử lý" : "Đổi"}</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </>
      )}
      {subTarget && (
        <HappySchoolCellSubstituteDialog
          schoolId={schoolId} runId={runId} weekId={weekId} target={subTarget} grid={grid} classes={classes} subjects={subjects}
          teachers={teachers} isBusyAt={isBusyAt} periodNotes={periodNotes}
          onClose={() => setSubTarget(null)}
          onSaved={() => { setSubTarget(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
};

const th = { border: "1px solid #e5e7eb", padding: "4px 8px", background: "#f9fafb", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "4px 8px", textAlign: "center" };
const dayBtn = { padding: "6px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 600 };
const dayBtnActive = { ...dayBtn, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3" };
const addBtn = { padding: "7px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const actBtn = { padding: "4px 12px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 };
const actBtnGhost = { ...actBtn, background: "#fff", color: "#0ea5a3", border: "1px solid #0ea5a3" };

export default HappySchoolSubstituteBoard;
